import { apiClient } from './apiClient';
import {
  normalizeAgentsResponse,
  normalizePlansResponse,
  normalizeUsersResponse,
} from './normalizers';

const base = '/api/admin';

export const adminService = {
  listUsers: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).reduce((acc, [k, v]) => {
        if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
        return acc;
      }, {})
    ).toString();
    const url = qs ? `${base}/users?${qs}` : `${base}/users`;
    return apiClient.get(url).then(normalizeUsersResponse);
  },
  listPlans: () => apiClient.get(`${base}/plans`).then(normalizePlansResponse),
  mutatePlan: payload => apiClient.post(`${base}/plans`, payload),
  createUser: user => apiClient.post(`${base}/users`, user).then(normalizeUsersResponse),
  updateUser: (id, user) =>
    apiClient.put(`${base}/users/${id}`, user).then(normalizeUsersResponse),
  deleteUser: id => apiClient.del(`${base}/users/${id}`),
  sendPaymentLink: (id, data) => apiClient.post(`${base}/users/${id}/payment-link`, data),
  sendPasswordReset: id => apiClient.post(`${base}/users/${id}/send-password-reset`),
  createAgentForUser: (id, agent) => apiClient.post(`${base}/users/${id}/agent`, agent),
  getDefaults: () => apiClient.get(`${base}/defaults`),
  updateDefaults: data => apiClient.post(`${base}/defaults`, data),
  // Provider management
  getProviders: () => apiClient.get(`${base}/providers`),
  mutateProvider: payload => apiClient.post(`${base}/providers`, payload),
  deleteProvider: id => apiClient.post(`${base}/providers`, { action: 'delete', id }),
  // User-specific methods
  getUserPayments: userId => apiClient.get(`${base}/users/${userId}/payments`),
  getUserAgents: userId =>
    apiClient.get(`${base}/users/${userId}/agents`).then(normalizeAgentsResponse),
  updateUserAgent: (userId, agentId, data) =>
    apiClient.put(`${base}/users/${userId}/agents/${agentId}`, data),
  deleteUserAgent: (userId, agentId) => apiClient.del(`${base}/users/${userId}/agents/${agentId}`),
  getUserIntegrations: userId => apiClient.get(`${base}/integrations/${userId}`),
  updateUserIntegrations: (userId, data) => apiClient.post(`${base}/integrations/${userId}`, data),
  // Articles
  listArticles: () => apiClient.get(`${base}/articles`),
  createArticle: article => apiClient.post(`${base}/articles`, article),
  updateArticle: (id, article) => apiClient.put(`${base}/articles/${id}`, article),
  deleteArticle: id => apiClient.del(`${base}/articles/${id}`),
  // Languages
  listLanguages: () => apiClient.get(`${base}/languages`),
  createLanguage: language => apiClient.post(`${base}/languages`, language),
  updateLanguage: (id, language) => apiClient.put(`${base}/languages/${id}`, language),
  deleteLanguage: id => apiClient.del(`${base}/languages/${id}`),
  // Use Cases
  listUseCases: () => apiClient.get(`${base}/use-cases`),
  createUseCase: useCase => apiClient.post(`${base}/use-cases`, useCase),
  updateUseCase: (id, useCase) => apiClient.put(`${base}/use-cases/${id}`, useCase),
  deleteUseCase: id => apiClient.del(`${base}/use-cases/${id}`),
  // Dashboard
  getDashboardStats: () => apiClient.get(`${base}/dashboard/stats`),
  // Per-user usage summary (costs & credits)
  getUserUsageSummary: userId => apiClient.get(`${base}/users/${userId}/usage-summary`),
  // Feature Toggles
  listFeatureToggles: (level = 'all') =>
    apiClient.get(`${base}/feature-toggles${level !== 'all' ? `?level=${level}` : ''}`),
  listRootFeatureToggles: () => apiClient.get(`${base}/toggles/root`),
  createFeatureToggle: toggle => apiClient.post(`${base}/feature-toggles`, toggle),
  createRootFeatureToggle: toggle => apiClient.post(`${base}/toggles/root`, toggle),
  updateFeatureToggle: (id, toggle) => apiClient.put(`${base}/toggles/root/${id}`, toggle),
  deleteFeatureToggle: id => apiClient.del(`${base}/feature-toggles/${id}`),
  // Plan-specific Feature Toggles
  getPlanFeatureToggles: planId => apiClient.get(`${base}/plans/${planId}/feature-toggles`),
  updatePlanFeatureToggle: (planId, toggleKey, enabled, inherit = false) =>
    apiClient.post(`${base}/plans/${planId}/feature-toggles/${toggleKey}`, { enabled, inherit }),
  deletePlanFeatureToggle: (planId, toggleKey) =>
    apiClient.del(`${base}/plans/${planId}/feature-toggles/${toggleKey}`),
  // User-specific Feature Toggles
  getUserFeatureToggles: userId => apiClient.get(`${base}/toggles/user/${userId}`),
  updateUserFeatureToggle: (userId, featureKey, enabled, inherit = false) =>
    apiClient.post(`${base}/toggles/user/${userId}/${featureKey}`, { enabled, inherit }),
  deleteUserFeatureToggle: (userId, featureKey) =>
    apiClient.del(`${base}/toggles/user/${userId}/${featureKey}`),
  // Call Me Settings
  getCallMeSettings: () => apiClient.get(`${base}/callme/settings`),
  updateCallMeSettings: settings => apiClient.post(`${base}/callme/settings`, settings),
  // Root Provider Configs
  getRootProviderConfigs: (includeDecrypted = false) =>
    apiClient.get(
      `${base}/root-provider-configs${includeDecrypted ? '?includeDecrypted=true' : ''}`
    ),
  saveRootProviderConfig: config => apiClient.post(`${base}/root-provider-configs`, config),
  deleteRootProviderConfig: (providerType, provider) =>
    apiClient.del(`${base}/root-provider-configs/${providerType}/${provider}`),
  getUtilityModelConfig: () => apiClient.get(`${base}/utility-model-config`),
  saveUtilityModelConfig: config => apiClient.post(`${base}/utility-model-config`, config),
  testUtilityModelConfig: (config = null) =>
    apiClient.post(`${base}/utility-model-config/test`, config ? { config } : {}),
  getFireworksModels: (purpose = 'utility') =>
    apiClient.get(`${base}/fireworks-models?purpose=${encodeURIComponent(purpose)}`),
  getEmbeddingsConfig: () => apiClient.get(`${base}/embeddings-config`),
  saveEmbeddingsConfig: config => apiClient.post(`${base}/embeddings-config`, config),
  testEmbeddingsConfig: (config = {}) =>
    apiClient.post(`${base}/embeddings-config/test`, config),
  getRerankConfig: () => apiClient.get(`${base}/rerank-config`),
  saveRerankConfig: config => apiClient.post(`${base}/rerank-config`, config),
  getCallCampaigns: (query = '') => apiClient.get(`${base}/call-campaigns${query}`),
  // CloudWatch Logs (on-demand Refresh)
  getLogSources: () => apiClient.get(`${base}/logs/sources`),
  getLogEvents: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).reduce((acc, [k, v]) => {
        if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
        return acc;
      }, {})
    ).toString();
    return apiClient.get(`${base}/logs/events${qs ? `?${qs}` : ''}`);
  },
};

export default adminService;
